import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { deriveAgents, groupByAgent } from './followupHelpers';
import type {
  AgentOption,
  FollowupStats,
  PendingFollowup,
  PriorityFilter,
} from './types';

function buildListParams(
  filter: PriorityFilter,
  agentId: string
): Record<string, string> {
  const params: Record<string, string> = {};
  if (filter === 'overdue') params.is_overdue = 'true';
  else if (filter !== 'all') params.priority = filter;
  if (agentId) params.user_id = agentId;
  return params;
}

function filterBySearch(
  followups: PendingFollowup[],
  search: string
): PendingFollowup[] {
  const q = search.trim().toLowerCase();
  if (!q) return followups;
  return followups.filter(
    (f) =>
      f.pos_name?.toLowerCase().includes(q) ||
      f.pos_address?.toLowerCase().includes(q) ||
      f.pos_city?.toLowerCase().includes(q)
  );
}

async function loadFollowups(filter: PriorityFilter, agentFilter: string) {
  const listParams = buildListParams(filter, agentFilter);
  const statsParams: Record<string, string> = {};
  if (agentFilter) statsParams.user_id = agentFilter;

  const [followupsRes, statsRes] = await Promise.all([
    api.get<PendingFollowup[]>('/followups', { params: listParams }),
    api.get<FollowupStats>('/followups/stats', { params: statsParams }),
  ]);

  // Agent options must stay unfiltered by user_id so the select does not collapse.
  const agents = agentFilter ? null : deriveAgents(followupsRes.data);
  return { followups: followupsRes.data, stats: statsRes.data, agents };
}

function useFollowupView(
  followups: PendingFollowup[],
  search: string,
  groupByAgentOn: boolean,
  agentFilter: string,
  agents: AgentOption[]
) {
  const selectedAgentName = useMemo(() => {
    if (!agentFilter) return null;
    return agents.find((a) => a.id === agentFilter)?.name ?? null;
  }, [agents, agentFilter]);

  const visibleFollowups = useMemo(
    () => filterBySearch(followups, search),
    [followups, search]
  );

  const agentGroups = useMemo(
    () => (groupByAgentOn ? groupByAgent(visibleFollowups) : null),
    [groupByAgentOn, visibleFollowups]
  );

  return { selectedAgentName, visibleFollowups, agentGroups };
}

/**
 * Agent options only contain agents that currently have pending tasks. `keepId` (the logged-in user)
 * is exempt from the reset, otherwise the "My tasks" chip would silently jump back to "All agents"
 * for an agent whose list is empty.
 */
function useStaleAgentReset(
  agentFilter: string,
  agents: AgentOption[],
  setAgentFilter: (id: string) => void,
  keepId?: string
) {
  useEffect(() => {
    if (
      agentFilter &&
      agentFilter !== keepId &&
      agents.length > 0 &&
      !agents.some((a) => a.id === agentFilter)
    ) {
      setAgentFilter('');
    }
  }, [agents, agentFilter, setAgentFilter, keepId]);
}

export function useFollowups(keepAgentId?: string) {
  const [followups, setFollowups] = useState<PendingFollowup[]>([]);
  const [stats, setStats] = useState<FollowupStats | null>(null);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<PriorityFilter>('all');
  const [agentFilter, setAgentFilter] = useState('');
  const [groupByAgentOn, setGroupByAgentOn] = useState(false);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await loadFollowups(filter, agentFilter);
      setFollowups(result.followups);
      setStats(result.stats);
      if (result.agents) setAgents(result.agents);
    } catch (error) {
      console.error('Failed to fetch follow-ups:', error);
      toast.error('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  }, [filter, agentFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useStaleAgentReset(agentFilter, agents, setAgentFilter, keepAgentId);

  const view = useFollowupView(
    followups,
    search,
    groupByAgentOn,
    agentFilter,
    agents
  );

  return {
    followups, stats, agents, loading, fetchData,
    filter, setFilter, agentFilter, setAgentFilter,
    groupByAgentOn, setGroupByAgentOn, search, setSearch,
    ...view,
  };
}
